import React, { useState, useEffect, useCallback, useRef } from 'react';
import './index.css';
import visaLogo from './assets/visa.svg';
import mastercardLogo from './assets/mastercard.svg';
import troyLogo from './assets/troy.svg';
import axios from 'axios';
import {
  Calendar,
  CalendarPlus,
  ClipboardList,
  Clock,
  MapPin,
  ChevronRight,
  Bell,
  CheckCircle2,
  X,
  Loader2,
  AlertCircle,
  CreditCard,
  Landmark,
  FileText,
  Upload,
  Copy,
  Check,
  ScrollText,
  ArrowLeft,
  Wrench,
  Phone
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, startOfToday, isSameDay, isToday, isWeekend } from 'date-fns';
import { tr } from 'date-fns/locale';

const API_BASE_URL = 'https://app.yanibasimdakaratay.com';

axios.defaults.baseURL = API_BASE_URL;

const getAuthHeader = () => {
  const raw = localStorage.getItem('token');
  if (!raw) return {};
  try {
    return { Authorization: 'Bearer ' + JSON.parse(raw) };
  } catch {
    return {};
  }
};

type Court = {
  id: number;
  ad: string;
  tip: string;
  lokasyon: string;
  fiyat: number;
  gorsel: string | null;
};

type Slot = {
  id: number;
  time: string;
  bitis: string;
  fiyat: number;
  isAvailable: boolean;
};

type Duyuru = {
  id: number;
  baslik: string;
  icerik: string;
  tip: 'BILGI' | 'UYARI' | 'BAKIM';
  baslangic_tarihi: string | null;
  bitis_tarihi: string | null;
  created_at: string;
};

type Kural = {
  id: number;
  baslik: string;
  icerik: string;
  tip: 'PRIMARY' | 'AMBER' | 'RED' | 'BLUE';
  sira: number;
};

type Tatil = {
  sebep: string;
  baslangic_tarihi: string | null;
  bitis_tarihi: string | null;
  tekrar_haftalik_gunler: string | null;
};

type UserInfo = {
  adi: string;
  soyadi: string;
  tc: string | null;
  dogum_tarihi: string | null;
  profile_photo_url: string | null;
};

type Reservation = {
  id: number;
  kort: Court;
  tarih: string;
  tarih_text: string;
  baslangic_saat: string;
  bitis_saat: string;
  durum: string;
  son_red_sebebi: string | null;
  kalan_yukleme_hakki: number | null;
};

type IbanInfo = {
  banka: string;
  alici: string;
  iban: string;
};

const COURT_IMAGE_FALLBACK = 'https://images.unsplash.com/photo-1595435063823-ac4ad97f44d8?q=80&w=600&auto=format&fit=crop';

type CardBrand = 'visa' | 'mastercard' | 'troy' | null;

const detectCardBrand = (kartNo: string): CardBrand => {
  const d = kartNo.replace(/\s+/g, '');
  if (/^4/.test(d)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(d)) return 'mastercard';
  if (/^9792/.test(d)) return 'troy';
  return null;
};

const luhnGecerliMi = (kartNo: string): boolean => {
  const d = kartNo.replace(/\s+/g, '');
  if (!/^[0-9]{13,19}$/.test(d)) return false;
  let sum = 0;
  let alt = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = +d[i];
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

function CardBrandIcon({ brand }: { brand: CardBrand }) {
  if (brand === 'visa') return <img src={visaLogo} alt="Visa" className="h-6 w-auto object-contain" draggable={false} />;
  if (brand === 'mastercard') return <img src={mastercardLogo} alt="Mastercard" className="h-7 w-auto object-contain" draggable={false} />;
  if (brand === 'troy') return <img src={troyLogo} alt="Troy" className="h-6 w-auto object-contain" draggable={false} />;
  return null;
}

export function App() {
  const [activeTab, setActiveTab] = useState<'home' | 'reservations'>('home');
  const [selectedDate, setSelectedDate] = useState(startOfToday());
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isReserving, setIsReserving] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);

  const [user, setUser] = useState<UserInfo | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [bannerBaslik, setBannerBaslik] = useState<string>('Günü Keşfet');
  const [bannerMetin, setBannerMetin] = useState<string>('Hazır mısın? Raketini kap ve gel!');
  const [destek, setDestek] = useState<{ telefon: string; dahili: string }>({ telefon: '+903323501313', dahili: '2233' });
  const [ibanAktif, setIbanAktif] = useState<boolean>(true);
  const [bakim, setBakim] = useState<{ aktif: boolean; mesaj: string }>({ aktif: false, mesaj: '' });
  const [rezervasyonGunSayisi, setRezervasyonGunSayisi] = useState<number>(7);
  const [galeri, setGaleri] = useState<{ id: number; dosya_url: string; baslik: string | null }[]>([]);
  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [courts, setCourts] = useState<Court[]>([]);
  const [courtsLoading, setCourtsLoading] = useState(true);
  const [tatilBilgisi, setTatilBilgisi] = useState<Tatil | null>(null);

  const [duyurular, setDuyurular] = useState<Duyuru[]>([]);
  const [duyurularLoading, setDuyurularLoading] = useState(false);
  const [showDuyurular, setShowDuyurular] = useState(false);
  const [lastReadDuyuruId, setLastReadDuyuruId] = useState<number>(() => {
    const raw = localStorage.getItem('tenis_kortu_last_read_duyuru_id');
    return raw ? parseInt(raw, 10) || 0 : 0;
  });
  const unreadCount = duyurular.filter(d => d.id > lastReadDuyuruId).length;

  const [showKurallar, setShowKurallar] = useState(false);
  const [kurallar, setKurallar] = useState<Kural[]>([]);
  const [kurallarLoading, setKurallarLoading] = useState(false);

  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  const [kortYogunluk, setKortYogunluk] = useState<Record<number, { oran: number; dolu: number; toplam: number; tatil: boolean }>>({});

  const [modalStep, setModalStep] = useState<'slot' | 'method' | 'consent' | 'card' | 'iban'>('slot');
  const [pendingMethod, setPendingMethod] = useState<'card' | 'iban' | null>(null);
  const [kurallarKabul, setKurallarKabul] = useState(false);
  const [cardForm, setCardForm] = useState({
    kart_no: '',
    kart_adi_soyadi: '',
    son_kullanma_ay: '',
    son_kullanma_yil: '',
    cvv: '',
  });
  const [cardErrors, setCardErrors] = useState<Partial<Record<'kart_no' | 'kart_adi_soyadi' | 'son_kullanma_ay' | 'son_kullanma_yil' | 'cvv', string>>>({});
  const yilRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);

  const validateCard = useCallback((): typeof cardErrors => {
    const errs: typeof cardErrors = {};
    const kartDigits = cardForm.kart_no.replace(/\s+/g, '');
    if (!kartDigits) errs.kart_no = 'Kart numarası zorunludur.';
    else if (kartDigits.length < 13) errs.kart_no = 'Kart numarası eksik.';
    else if (!luhnGecerliMi(kartDigits)) errs.kart_no = 'Geçersiz kart numarası.';
    else if (!detectCardBrand(kartDigits)) errs.kart_no = 'Sadece Visa, Mastercard veya Troy kabul edilir.';

    const isim = cardForm.kart_adi_soyadi.trim();
    if (!isim) errs.kart_adi_soyadi = 'Kart üzerindeki isim zorunludur.';
    else if (isim.length < 3) errs.kart_adi_soyadi = 'İsim çok kısa.';
    else if (!/\s/.test(isim)) errs.kart_adi_soyadi = 'Ad ve soyad birlikte yazılmalı.';

    const ay = parseInt(cardForm.son_kullanma_ay, 10);
    if (!cardForm.son_kullanma_ay) errs.son_kullanma_ay = 'Ay zorunlu.';
    else if (!(ay >= 1 && ay <= 12)) errs.son_kullanma_ay = '01-12 arası.';

    const yy = parseInt(cardForm.son_kullanma_yil, 10);
    const buYilYY = new Date().getFullYear() % 100;
    const buAy = new Date().getMonth() + 1;
    if (!cardForm.son_kullanma_yil || cardForm.son_kullanma_yil.length !== 2) errs.son_kullanma_yil = 'YY formatı.';
    else if (yy < buYilYY) errs.son_kullanma_yil = 'Kart süresi dolmuş.';
    else if (yy === buYilYY && !errs.son_kullanma_ay && ay < buAy) errs.son_kullanma_ay = 'Kart süresi dolmuş.';

    if (!cardForm.cvv) errs.cvv = 'CVV zorunlu.';
    else if (!/^[0-9]{3,4}$/.test(cardForm.cvv)) errs.cvv = '3-4 haneli olmalı.';

    return errs;
  }, [cardForm]);
  const [paymentHtml, setPaymentHtml] = useState<string | null>(null);
  const [holdId, setHoldId] = useState<number | null>(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState<number | null>(null); // epoch ms
  const [holdRemaining, setHoldRemaining] = useState<number>(0); // saniye
  const [acquiringHold, setAcquiringHold] = useState(false);
  const [ibanInfo, setIbanInfo] = useState<IbanInfo | null>(null);
  const [dekontFile, setDekontFile] = useState<File | null>(null);
  const [ibanSuccess, setIbanSuccess] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [reUploadFor, setReUploadFor] = useState<Reservation | null>(null);
  const [reUploadFile, setReUploadFile] = useState<File | null>(null);
  const [reUploading, setReUploading] = useState(false);

  const [reservationFilter, setReservationFilter] = useState<'TUMU' | 'OPEN' | 'PENDING_APPROVAL' | 'REJECTED' | 'CANCELLED'>('OPEN');

  const copyToClipboard = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(prev => (prev === field ? null : prev)), 1500);
    } catch {
      showError('Kopyalanamadı.');
    }
  };
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Reservation | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      localStorage.setItem('token', JSON.stringify(token));
    }
    setHasToken(!!localStorage.getItem('token'));
  }, []);

  const showError = useCallback((msg: string) => {
    setErrorMessage(msg);
    window.setTimeout(() => setErrorMessage(null), 4000);
  }, []);

  const extractError = (e: unknown, fallback: string) => {
    if (axios.isAxiosError(e)) {
      const data = e.response?.data as { message?: string } | undefined;
      if (data?.message) return data.message;
      if (e.response?.status === 401) return 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
    }
    return fallback;
  };

  const fetchCourts = useCallback(async () => {
    if (!hasToken) return;
    setCourtsLoading(true);
    try {
      const res = await axios.get<{
        items: Court[];
        user: UserInfo;
        banner_url: string | null;
        banner_baslik: string | null;
        banner_metin: string | null;
        iban_aktif?: boolean;
        bakim_modu?: boolean;
        bakim_mesaji?: string;
        rezervasyon_gun_sayisi?: number;
        galeri?: { id: number; dosya_url: string; baslik: string | null }[];
        destek: { telefon: string; dahili: string };
      }>('/api/tenis-kortu/kortlar', {
        headers: getAuthHeader(),
      });
      setCourts(res.data.items);
      if (res.data.user) setUser(res.data.user);
      if (res.data.banner_url) setBannerUrl(res.data.banner_url);
      if (res.data.banner_baslik) setBannerBaslik(res.data.banner_baslik);
      if (res.data.banner_metin) setBannerMetin(res.data.banner_metin);
      setIbanAktif(res.data.iban_aktif !== false);
      setBakim({ aktif: res.data.bakim_modu === true, mesaj: res.data.bakim_mesaji ?? '' });
      if (typeof res.data.rezervasyon_gun_sayisi === 'number' && res.data.rezervasyon_gun_sayisi > 0) {
        setRezervasyonGunSayisi(res.data.rezervasyon_gun_sayisi);
      }
      if (Array.isArray(res.data.galeri)) setGaleri(res.data.galeri);
      if (res.data.destek) setDestek(res.data.destek);
    } catch (e) {
      showError(extractError(e, 'Kortlar yüklenemedi.'));
    } finally {
      setCourtsLoading(false);
    }
  }, [hasToken, showError]);

  const fetchSlots = useCallback(async (court: Court, date: Date) => {
    setSlotsLoading(true);
    setSlots([]);
    setTatilBilgisi(null);
    try {
      const res = await axios.get<{ items: Slot[]; tatil?: Tatil }>('/api/tenis-kortu/saatler', {
        headers: getAuthHeader(),
        params: {
          kort_id: court.id,
          tarih: format(date, 'yyyy-MM-dd'),
        },
      });
      setSlots(res.data.items);
      if (res.data.tatil) setTatilBilgisi(res.data.tatil);
    } catch (e) {
      showError(extractError(e, 'Saatler yüklenemedi.'));
    } finally {
      setSlotsLoading(false);
    }
  }, [showError]);

  const fetchKortYogunluk = useCallback(async (tarih: string) => {
    try {
      const res = await axios.get<{ items: { kort_id: number; oran: number; dolu: number; toplam: number; tatil: boolean }[] }>(
        `/api/tenis-kortu/yogunluk-kort?tarih=${tarih}`,
        { headers: getAuthHeader() }
      );
      const map: Record<number, { oran: number; dolu: number; toplam: number; tatil: boolean }> = {};
      for (const it of res.data.items) {
        map[it.kort_id] = { oran: it.oran, dolu: it.dolu, toplam: it.toplam, tatil: it.tatil };
      }
      setKortYogunluk(map);
    } catch {
      // silent
    }
  }, []);

  const fetchKurallar = useCallback(async () => {
    setKurallarLoading(true);
    try {
      const res = await axios.get<{ items: Kural[] }>('/api/tenis-kortu/kurallar', { headers: getAuthHeader() });
      setKurallar(res.data.items);
    } catch (e) {
      showError(extractError(e, 'Kurallar yüklenemedi.'));
    } finally {
      setKurallarLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (modalStep === 'consent' && kurallar.length === 0 && !kurallarLoading) {
      fetchKurallar();
    }
  }, [modalStep, kurallar.length, kurallarLoading, fetchKurallar]);

  const fetchDuyurular = useCallback(async () => {
    setDuyurularLoading(true);
    try {
      const res = await axios.get<{ items: Duyuru[] }>('/api/tenis-kortu/duyurular', { headers: getAuthHeader() });
      setDuyurular(res.data.items);
    } catch (e) {
      showError(extractError(e, 'Duyurular yüklenemedi.'));
    } finally {
      setDuyurularLoading(false);
    }
  }, [showError]);

  const fetchReservations = useCallback(async () => {
    if (!hasToken) return;
    setReservationsLoading(true);
    try {
      const res = await axios.get<{ items: Reservation[] }>('/api/tenis-kortu/rezervasyonlarim', {
        headers: getAuthHeader(),
      });
      setReservations(res.data.items);
    } catch (e) {
      showError(extractError(e, 'Rezervasyonlar yüklenemedi.'));
    } finally {
      setReservationsLoading(false);
    }
  }, [hasToken, showError]);

  useEffect(() => {
    fetchCourts();
    if (hasToken) fetchDuyurular();
  }, [fetchCourts, fetchDuyurular, hasToken]);

  useEffect(() => {
    if (!hasToken) return;
    fetchKortYogunluk(format(selectedDate, 'yyyy-MM-dd'));
  }, [hasToken, selectedDate, fetchKortYogunluk]);

  useEffect(() => {
    if (activeTab === 'reservations') {
      fetchReservations();
    }
  }, [activeTab, fetchReservations]);

  useEffect(() => {
    if (showReservationModal && selectedCourt) {
      fetchSlots(selectedCourt, selectedDate);
      setSelectedSlot(null);
      setModalStep('slot');
    }
  }, [showReservationModal, selectedCourt, selectedDate, fetchSlots]);

  useEffect(() => {
    if (!showReservationModal) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [showReservationModal]);

  const releaseHold = useCallback(async (id: number | null) => {
    if (!id) return;
    try {
      await axios.post('/api/tenis-kortu/kilit-bosalt', { hold_id: id }, { headers: getAuthHeader() });
    } catch {
      // sessiz — süresi zaten dolacak
    }
  }, []);

  const clearHold = useCallback((options?: { release?: boolean }) => {
    if (options?.release && holdId) {
      releaseHold(holdId);
    }
    setHoldId(null);
    setHoldExpiresAt(null);
    setHoldRemaining(0);
  }, [holdId, releaseHold]);

  const closeReservationModal = useCallback(() => {
    setShowReservationModal(false);
    setSelectedSlot(null);
    setModalStep('slot');
    setPendingMethod(null);
    setKurallarKabul(false);
    setCardForm({ kart_no: '', kart_adi_soyadi: '', son_kullanma_ay: '', son_kullanma_yil: '', cvv: '' });
    setDekontFile(null);
    clearHold({ release: true });
  }, [clearHold]);

  const acquireHold = useCallback(async (): Promise<boolean> => {
    if (!selectedSlot || !selectedCourt) return false;
    setAcquiringHold(true);
    try {
      const res = await axios.post<{ hold_id: number; expires_at: string; sure_saniye: number }>(
        '/api/tenis-kortu/kilitle',
        {
          kort_id: selectedCourt.id,
          tarih: format(selectedDate, 'yyyy-MM-dd'),
          baslangic_saat: selectedSlot.time,
        },
        { headers: getAuthHeader() }
      );
      setHoldId(res.data.hold_id);
      const exp = new Date(res.data.expires_at).getTime();
      setHoldExpiresAt(exp);
      setHoldRemaining(Math.max(0, Math.round((exp - Date.now()) / 1000)));
      return true;
    } catch (e) {
      showError(extractError(e, 'Slot şu anda tutulamadı, lütfen başka bir saat seçin.'));
      return false;
    } finally {
      setAcquiringHold(false);
    }
  }, [selectedCourt, selectedDate, selectedSlot, showError]);

  useEffect(() => {
    if (!holdExpiresAt) return;
    const tick = () => {
      const rem = Math.max(0, Math.round((holdExpiresAt - Date.now()) / 1000));
      setHoldRemaining(rem);
      if (rem <= 0) {
        setHoldId(null);
        setHoldExpiresAt(null);
        setShowReservationModal(false);
        setSelectedSlot(null);
        setModalStep('slot');
        setPendingMethod(null);
        setKurallarKabul(false);
        setCardForm({ kart_no: '', kart_adi_soyadi: '', son_kullanma_ay: '', son_kullanma_yil: '', cvv: '' });
        showError('Kilit süreniz doldu. Slot serbest bırakıldı, lütfen tekrar deneyin.');
      }
    };
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [holdExpiresAt, showError]);

  const fetchIbanBilgisi = useCallback(async () => {
    if (ibanInfo) return;
    try {
      const res = await axios.get<{ iban: IbanInfo }>('/api/tenis-kortu/iban-bilgisi', {
        headers: getAuthHeader(),
      });
      setIbanInfo(res.data.iban);
    } catch (e) {
      showError(extractError(e, 'IBAN bilgisi alınamadı.'));
    }
  }, [ibanInfo, showError]);

  const handleIbanSubmit = async () => {
    if (!selectedSlot || !selectedCourt || !dekontFile) {
      showError('Lütfen dekontu yükleyin.');
      return;
    }

    setIsReserving(true);
    try {
      const formData = new FormData();
      formData.append('kort_id', String(selectedCourt.id));
      formData.append('tarih', format(selectedDate, 'yyyy-MM-dd'));
      formData.append('baslangic_saat', selectedSlot.time);
      formData.append('dekont', dekontFile);

      await axios.post('/api/tenis-kortu/odeme/iban', formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });

      closeReservationModal();
      setIbanSuccess(true);
    } catch (e) {
      showError(extractError(e, 'Dekont gönderilemedi.'));
    } finally {
      setIsReserving(false);
    }
  };

  const handleStartPayment = async () => {
    if (!selectedSlot || !selectedCourt) return;

    const errs = validateCard();
    setCardErrors(errs);
    if (Object.keys(errs).length > 0) {
      const ilkHata = Object.values(errs)[0];
      showError(ilkHata ?? 'Lütfen kart bilgilerini kontrol edin.');
      return;
    }

    setIsReserving(true);
    try {
      const res = await axios.post<{ html: string; referans_no: string }>(
        '/api/tenis-kortu/odeme/baslat',
        {
          kort_id: selectedCourt.id,
          tarih: format(selectedDate, 'yyyy-MM-dd'),
          baslangic_saat: selectedSlot.time,
          hold_id: holdId,
          kart_no: cardForm.kart_no.replace(/\s+/g, ''),
          kart_adi_soyadi: cardForm.kart_adi_soyadi,
          son_kullanma_ay: cardForm.son_kullanma_ay,
          son_kullanma_yil: cardForm.son_kullanma_yil,
          cvv: cardForm.cvv,
        },
        { headers: getAuthHeader() }
      );
      setPaymentHtml(res.data.html);
    } catch (e) {
      // MPI/backend hatasında slot backend'de CANCELLED'a çekildi.
      // Kullanıcıyı rıza ekranına döndür, yeni bir kilit almasına imkan ver.
      setHoldId(null);
      setHoldExpiresAt(null);
      setHoldRemaining(0);
      setModalStep('consent');
      showError(extractError(e, 'Ödeme başlatılamadı.'));
    } finally {
      setIsReserving(false);
    }
  };

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data as { source?: string; status?: string; message?: string } | undefined;
      if (!data || data.source !== 'tenis-kortu-payment') return;

      if (data.status === 'success') {
        setPaymentHtml(null);
        setShowReservationModal(false);
        setSelectedSlot(null);
        setModalStep('slot');
        setPendingMethod(null);
        setCardForm({ kart_no: '', kart_adi_soyadi: '', son_kullanma_ay: '', son_kullanma_yil: '', cvv: '' });
        // Ödeme başarılı — kilit backend'de OPEN'a çevrildi, sadece state'i temizle.
        setHoldId(null);
        setHoldExpiresAt(null);
        setHoldRemaining(0);
        setReservationSuccess(true);
      } else if (data.status === 'failed') {
        setPaymentHtml(null);
        // Ödeme başarısız — backend zaten slot'u serbest bıraktı.
        setHoldId(null);
        setHoldExpiresAt(null);
        setHoldRemaining(0);
        showError(data.message || 'Ödeme tamamlanamadı.');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [showError]);

  const handleReUpload = async () => {
    if (!reUploadFor || !reUploadFile) {
      showError('Lütfen yeni dekontu seçin.');
      return;
    }
    setReUploading(true);
    try {
      const formData = new FormData();
      formData.append('rezervasyon_id', String(reUploadFor.id));
      formData.append('dekont', reUploadFile);

      await axios.post('/api/tenis-kortu/odeme/iban-tekrar', formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });

      setReUploadFor(null);
      setReUploadFile(null);
      fetchReservations();
      setIbanSuccess(true);
    } catch (e) {
      showError(extractError(e, 'Dekont gönderilemedi.'));
    } finally {
      setReUploading(false);
    }
  };

  const handleCancel = async (id: number) => {
    setCancellingId(id);
    try {
      await axios.post('/api/tenis-kortu/iptal', { id }, {
        headers: getAuthHeader(),
      });
      setReservations(prev => prev.map(r => (r.id === id ? { ...r, durum: 'CANCELLED' } : r)));
      setCancelTarget(null);
    } catch (e) {
      showError(extractError(e, 'İptal işlemi başarısız.'));
    } finally {
      setCancellingId(null);
    }
  };

  const dates = Array.from({ length: rezervasyonGunSayisi }).map((_, i) => addDays(startOfToday(), i));

  // Admin panelden bakım modu açıldıysa uygulama yerine bakım ekranı gösterilir.
  if (bakim.aktif) {
    return (
      <div className="flex flex-col min-h-screen bg-background text-text-main">
        <header className="bg-white border-b border-gray-100 shadow-sm">
          <div className="px-6 py-4 pt-[max(env(safe-area-inset-top),2.75rem)] grid grid-cols-3 items-center">
            <div className="flex justify-start">
              <button
                onClick={() => window.history.back()}
                className="p-2 bg-primary/5 rounded-full text-primary hover:bg-primary/10 transition"
                title="Geri"
                aria-label="Geri"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
            <div className="flex justify-center">
              <img
                src="https://yanibasimdakaratay.com/assets/images/resources/yanibasimda-logo.svg"
                alt="Yanı Başımda Karatay"
                className="h-10 w-auto"
                draggable={false}
              />
            </div>
            <div />
          </div>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
          <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
            <Wrench className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-xl font-bold">Bakım Çalışması</h1>
          <p className="text-sm text-text-muted max-w-sm whitespace-pre-wrap">
            {bakim.mesaj || 'Tenis kortu rezervasyon sistemi şu anda bakımdadır. Lütfen daha sonra tekrar deneyin.'}
          </p>
          {destek.telefon && (
            <a
              href={`tel:${destek.telefon}`}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-medium"
            >
              <Phone className="w-4 h-4" />
              {destek.telefon}{destek.dahili ? ` (Dahili ${destek.dahili})` : ''}
            </a>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background text-text-main relative">
      <header className="sticky top-0 bg-white border-b border-gray-100 shadow-sm z-30">
        <div className="px-6 py-4 pt-[max(env(safe-area-inset-top),2.75rem)] grid grid-cols-3 items-center">
          <div className="flex justify-start">
            <button
              onClick={() => window.history.back()}
              className="p-2 bg-primary/5 rounded-full text-primary hover:bg-primary/10 transition"
              title="Geri"
              aria-label="Geri"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
          <div className="flex justify-center">
            <img
              src="https://yanibasimdakaratay.com/assets/images/resources/yanibasimda-logo.svg"
              alt="Yanı Başımda Karatay"
              className="h-10 w-auto"
              draggable={false}
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowKurallar(true); fetchKurallar(); }}
              className="p-2 bg-primary/5 rounded-full text-primary hover:bg-primary/10 transition"
              title="Kurallar"
            >
              <ScrollText className="w-5 h-5" />
            </button>
            <div className="relative">
              <button
                onClick={() => {
                  setShowDuyurular(true);
                  fetchDuyurular();
                  if (duyurular.length > 0) {
                    const maxId = Math.max(...duyurular.map(d => d.id));
                    if (maxId > lastReadDuyuruId) {
                      setLastReadDuyuruId(maxId);
                      localStorage.setItem('tenis_kortu_last_read_duyuru_id', String(maxId));
                    }
                  }
                }}
                className="p-2 bg-primary/5 rounded-full text-primary hover:bg-primary/10 transition"
              >
                <Bell className="w-5 h-5" />
              </button>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 border-2 border-white rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto no-scrollbar">
        <div className="max-w-md mx-auto px-6 pt-6">
          <button
            type="button"
            onClick={() => setGalleryIndex(0)}
            className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-premium group block bg-gray-200"
          >
            {!bannerLoaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse" />
            )}
            <img
              key={bannerUrl ?? 'fallback'}
              src={bannerUrl ?? COURT_IMAGE_FALLBACK}
              onLoad={() => setBannerLoaded(true)}
              onError={() => setBannerLoaded(true)}
              className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${bannerLoaded ? 'opacity-100' : 'opacity-0'}`}
              alt="Banner"
            />
            <div className={`absolute inset-0 bg-gradient-to-t from-primary/90 to-transparent flex flex-col justify-end px-4 py-3 text-left transition-opacity duration-500 ${bannerLoaded ? 'opacity-100' : 'opacity-0'}`}>
              <span className="text-white/80 text-sm font-medium mb-1">{bannerBaslik}</span>
              <h2 className="text-xl font-bold text-white">{bannerMetin}</h2>
            </div>
          </button>
        </div>
        <div className="max-w-md mx-auto px-6 pt-6">
          <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('home')}
              className={`py-2.5 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 ${
                activeTab === 'home' ? 'bg-white text-primary shadow-sm' : 'text-text-muted hover:text-text-main'
              }`}
            >
              <CalendarPlus className="w-4 h-4" />
              Rezervasyon Yap
            </button>
            <button
              onClick={() => setActiveTab('reservations')}
              className={`py-2.5 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 ${
                activeTab === 'reservations' ? 'bg-white text-primary shadow-sm' : 'text-text-muted hover:text-text-main'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              Rezervasyonlarım
            </button>
          </div>
        </div>
        <div className="max-w-md mx-auto">
        {activeTab === 'home' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 space-y-8"
          >
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Tarih Seç</h3>
                <span className="text-primary font-medium text-sm">
                  {format(selectedDate, 'MMMM yyyy', { locale: tr })}
                </span>
              </div>
              <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-2">
                {dates.map((date, idx) => {
                  const isSelected = isSameDay(date, selectedDate);
                  const today = isToday(date);
                  const weekend = isWeekend(date);
                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedDate(date)}
                      className={`relative flex flex-col items-center min-w-15 py-3 rounded-xl transition-all border-2 ${
                        isSelected
                          ? 'bg-primary text-white border-primary shadow-lg shadow-primary/30 scale-105'
                          : today
                            ? 'bg-white text-text-main border-primary/40 hover:border-primary'
                            : weekend
                              ? 'bg-amber-50/50 text-text-main border-amber-200/60 hover:border-amber-300'
                              : 'bg-white text-text-muted border-gray-100 hover:border-primary/30'
                      }`}
                    >
                      <span className={`text-[10px] uppercase font-bold mb-0.5 tracking-wider ${
                        isSelected ? 'opacity-80' : weekend ? 'text-amber-600' : 'opacity-60'
                      }`}>
                        {format(date, 'eee', { locale: tr })}
                      </span>
                      <span className="text-lg font-black leading-none">
                        {format(date, 'd')}
                      </span>
                      {today && (
                        <span className={`text-[8px] font-bold uppercase mt-0.5 tracking-wider ${
                          isSelected ? 'text-white/90' : 'text-primary'
                        }`}>
                          Bugün
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4">Kortlar</h3>
              {courtsLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : courts.length === 0 ? (
                <div className="text-center py-10 text-text-muted text-sm">
                  Henüz açık kort bulunmamaktadır.
                </div>
              ) : (
                <div className="space-y-4 pb-4">
                  {courts.map(court => (
                    <motion.div
                      whileTap={{ scale: 0.98 }}
                      key={court.id}
                      onClick={() => {
                        setSelectedCourt(court);
                        setShowReservationModal(true);
                      }}
                      className="flex gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition cursor-pointer"
                    >
                      <div className="w-24 h-24 rounded-lg overflow-hidden shrink-0">
                        <img
                          src={court.gorsel || COURT_IMAGE_FALLBACK}
                          alt={court.ad}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div>
                          <div className="flex justify-between items-start">
                            <h4 className="font-bold text-base leading-tight">{court.ad}</h4>
                            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium">
                              {court.tip}
                            </span>
                          </div>
                          <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {court.lokasyon}
                          </p>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-primary font-bold">
                            {court.fiyat} ₺ <span className="text-[10px] font-normal text-text-muted">/saat</span>
                          </span>
                          <div className="flex items-center gap-2">
                            <KortDonut yog={kortYogunluk[court.id]} />
                            <div className="w-8 h-8 rounded-full bg-primary/5 flex items-center justify-center text-primary">
                              <ChevronRight className="w-5 h-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'reservations' && (
          <div className="p-6">
            <h3 className="font-bold text-xl mb-6">Rezervasyonlarım</h3>
            <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pb-1">
              {([
                { key: 'TUMU', label: 'Tümü' },
                { key: 'OPEN', label: 'Aktif' },
                { key: 'PENDING_APPROVAL', label: 'Bekleyen' },
                { key: 'REJECTED', label: 'Reddedilen' },
                { key: 'CANCELLED', label: 'İptal' },
              ] as const).map(f => {
                const count = f.key === 'TUMU' ? reservations.length : reservations.filter(r => r.durum === f.key).length;
                const isActive = reservationFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setReservationFilter(f.key)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'bg-white text-text-muted border border-gray-200 hover:border-primary/30'
                    }`}
                  >
                    {f.label} <span className="opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>

            {reservationsLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : reservations.filter(r => reservationFilter === 'TUMU' || r.durum === reservationFilter).length === 0 ? (
              <div className="text-center py-10 text-text-muted text-sm">
                Bu durumda rezervasyon bulunmamaktadır.
              </div>
            ) : (
              <div className="space-y-4">
                {reservations.filter(r => reservationFilter === 'TUMU' || r.durum === reservationFilter).map(r => (
                  <div
                    key={r.id}
                    className={`p-5 bg-white rounded-xl border-l-4 shadow-sm space-y-3 ${
                      r.durum === 'OPEN' ? 'border-primary' :
                      r.durum === 'PENDING_APPROVAL' ? 'border-amber-400' :
                      r.durum === 'REJECTED' ? 'border-red-400' :
                      'border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-bold text-lg">{r.kort?.ad ?? 'Kort'}</h4>
                      {r.durum === 'PENDING_APPROVAL' ? (
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-lg font-bold uppercase tracking-wider">
                          Onay Bekliyor
                        </span>
                      ) : r.durum === 'REJECTED' ? (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-lg font-bold uppercase tracking-wider">
                          Reddedildi
                        </span>
                      ) : r.durum === 'CANCELLED' ? (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-lg font-bold uppercase tracking-wider">
                          İptal
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-lg font-bold uppercase tracking-wider">
                          Aktif
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 text-text-muted">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span className="text-sm">{r.tarih_text}</span>
                      </div>
                      <div className="flex items-center gap-2 text-text-muted">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-sm">{r.baslangic_saat} - {r.bitis_saat}</span>
                      </div>
                    </div>
                    {r.durum === 'REJECTED' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2 text-sm">
                        {r.son_red_sebebi && (
                          <div>
                            <div className="text-xs font-bold text-red-800">Red Sebebi</div>
                            <div className="text-red-700">{r.son_red_sebebi}</div>
                          </div>
                        )}
                        {(r.kalan_yukleme_hakki ?? 0) > 0 ? (
                          <button
                            onClick={() => { setReUploadFor(r); setReUploadFile(null); fetchIbanBilgisi(); }}
                            className="w-full py-2 bg-primary text-white rounded-lg font-bold text-sm hover:bg-primary-hover transition flex items-center justify-center gap-2"
                          >
                            <Upload className="w-4 h-4" />
                            Yeniden Dekont Yükle
                            <span className="text-[10px] opacity-80">({r.kalan_yukleme_hakki} hakkınız kaldı)</span>
                          </button>
                        ) : (
                          <div className="text-xs text-red-700 text-center py-1">
                            Yükleme hakkınız doldu. İptal edip yeni rezervasyon yapın.
                          </div>
                        )}
                      </div>
                    )}
                    {r.durum !== 'CANCELLED' && (
                      <button
                        disabled={cancellingId === r.id}
                        onClick={() => setCancelTarget(r)}
                        className="w-full py-2 bg-gray-50 text-text-muted text-sm font-medium rounded-lg border border-gray-100 hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {cancellingId === r.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'İptal Et'
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        </div>
      </main>

      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setErrorMessage(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full rounded-t-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">Hata</h3>
                    <p className="text-sm text-text-muted mt-1">{errorMessage}</p>
                  </div>
                </div>
              </div>

              <div
                className="px-6 pt-2 flex"
                style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
              >
                <button
                  onClick={() => setErrorMessage(null)}
                  className="flex-1 py-4 rounded-xl font-bold bg-primary text-white hover:opacity-90 transition"
                >
                  Tamam
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showReservationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-full rounded-t-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="h-1.5 w-12 bg-gray-200 rounded-full mx-auto my-3 shrink-0" />

              <div className="p-6 overflow-y-auto overscroll-contain no-scrollbar">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-1">{selectedCourt?.ad}</h3>
                    <p className="text-text-muted flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> {format(selectedDate, 'd MMMM EEEE', { locale: tr })}
                    </p>
                  </div>
                  <button
                    disabled={isReserving}
                    onClick={closeReservationModal}
                    className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition disabled:opacity-50"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {modalStep === 'slot' && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        Saat Seçimi
                      </h4>
                      {!slotsLoading && slots.length > 0 && (
                        <span className="text-xs text-text-muted">
                          {slots.filter(s => s.isAvailable).length} boş / {slots.length}
                        </span>
                      )}
                    </div>

                    {!slotsLoading && slots.length > 0 && (
                      <div className="flex items-center gap-4 mb-4 text-[11px]">
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-md bg-white border-2 border-emerald-400" />
                          <span className="text-text-muted">Boş</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-md bg-primary" />
                          <span className="text-text-muted">Seçili</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-md bg-red-50 border-2 border-red-300" />
                          <span className="text-text-muted">Dolu</span>
                        </span>
                      </div>
                    )}

                    {slotsLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                      </div>
                    ) : tatilBilgisi ? (
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 text-center">
                        <Calendar className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                        <div className="font-bold text-amber-900">Bu tarih kapalı</div>
                        <div className="text-sm text-amber-700 mt-1">{tatilBilgisi.sebep}</div>
                        {tatilBilgisi.baslangic_tarihi && tatilBilgisi.bitis_tarihi
                          && tatilBilgisi.baslangic_tarihi !== tatilBilgisi.bitis_tarihi && (
                          <div className="text-xs text-amber-700 mt-1">
                            {tatilBilgisi.baslangic_tarihi} → {tatilBilgisi.bitis_tarihi}
                          </div>
                        )}
                      </div>
                    ) : slots.length === 0 ? (
                      <p className="text-center text-text-muted text-sm py-6">
                        Bu tarih için saat dilimi tanımlı değil.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2.5">
                        {slots.map(slot => {
                          const isSelected = selectedSlot?.id === slot.id;
                          const isPast = (() => {
                            if (!isToday(selectedDate)) return false;
                            const [h, m] = slot.time.split(':').map(Number);
                            const now = new Date();
                            return (h ?? 0) * 60 + (m ?? 0) <= now.getHours() * 60 + now.getMinutes();
                          })();
                          const bgCls = !slot.isAvailable
                            ? isPast
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              : 'bg-red-50 text-red-400 border-red-200 cursor-not-allowed'
                            : isSelected
                              ? 'bg-primary text-white border-primary shadow-md shadow-primary/30 scale-[1.03]'
                              : 'bg-white text-text-main border-emerald-300 hover:border-primary hover:bg-emerald-50/40';
                          const fiyatCls = !slot.isAvailable
                            ? isPast
                              ? 'text-gray-400'
                              : 'text-red-400 line-through'
                            : isSelected
                              ? 'text-white/90'
                              : 'text-primary';
                          const labelCls = !slot.isAvailable
                            ? isPast
                              ? 'text-gray-400'
                              : 'text-red-500'
                            : isSelected
                              ? 'text-white/70'
                              : 'text-emerald-600';
                          const labelText = !slot.isAvailable
                            ? isPast ? 'GEÇTİ' : 'DOLU'
                            : isSelected ? 'SEÇİLİ' : 'BOŞ';
                          return (
                            <button
                              key={slot.id}
                              disabled={!slot.isAvailable}
                              onClick={() => setSelectedSlot(slot)}
                              className={`relative py-2.5 rounded-lg text-sm font-bold transition-all border-2 ${bgCls}`}
                            >
                              <span className={!slot.isAvailable && !isPast ? 'line-through opacity-70' : ''}>
                                {slot.time}
                              </span>
                              <span className={`block text-[10px] font-bold mt-0.5 ${fiyatCls}`}>
                                {slot.fiyat} ₺
                              </span>
                              <span className={`block text-[8px] font-semibold mt-0.5 tracking-wider ${labelCls}`}>
                                {labelText}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {modalStep === 'method' && (
                  <div className="mb-8 space-y-4">
                    <button
                      type="button"
                      onClick={() => setModalStep('slot')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-primary-hover transition"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Saat seçimine dön
                    </button>
                    <h4 className="font-bold flex items-center gap-2">
                      Ödeme Yöntemi
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingMethod('card');
                        setModalStep('consent');
                      }}
                      className="w-full p-4 bg-white border-2 border-gray-100 rounded-xl hover:border-primary/50 transition flex items-center gap-4 text-left"
                    >
                      <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <CreditCard className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="font-bold">Kredi / Banka Kartı</div>
                        <div className="text-xs text-text-muted">3D Secure ile anında onay</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-muted" />
                    </button>
                    {ibanAktif && (
                      <button
                        type="button"
                        onClick={() => {
                          setDekontFile(null);
                          setPendingMethod('iban');
                          setModalStep('consent');
                        }}
                        className="w-full p-4 bg-white border-2 border-gray-100 rounded-xl hover:border-primary/50 transition flex items-center gap-4 text-left"
                      >
                        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <Landmark className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold">IBAN ile Havale / EFT</div>
                          <div className="text-xs text-text-muted">Ödeme sonrası dekont yüklemesi</div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-text-muted" />
                      </button>
                    )}
                  </div>
                )}

                {modalStep === 'consent' && (
                  <div className="mb-8 space-y-4">
                    <button
                      type="button"
                      onClick={() => {
                        setPendingMethod(null);
                        setKurallarKabul(false);
                        setModalStep('method');
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-primary-hover transition"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Ödeme yöntemine dön
                    </button>
                    <h4 className="font-bold flex items-center gap-2">
                      <ScrollText className="w-5 h-5 text-primary" />
                      Karatay Tenis Kortu Kullanım Kuralları
                    </h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-text-main space-y-3 max-h-72 overflow-y-auto">
                      {kurallarLoading ? (
                        <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
                      ) : kurallar.length === 0 ? (
                        <p className="text-center text-text-muted py-6">Henüz tanımlı kural bulunmamaktadır.</p>
                      ) : (
                        kurallar.map(k => {
                          const renk = k.tip === 'AMBER' ? 'border-amber-200 bg-amber-50'
                            : k.tip === 'RED' ? 'border-red-200 bg-red-50'
                            : k.tip === 'BLUE' ? 'border-blue-200 bg-blue-50'
                            : 'border-primary/20 bg-primary/5';
                          const baslikRenk = k.tip === 'AMBER' ? 'text-amber-900'
                            : k.tip === 'RED' ? 'text-red-900'
                            : k.tip === 'BLUE' ? 'text-blue-900'
                            : 'text-primary';
                          const metinRenk = k.tip === 'AMBER' ? 'text-amber-900'
                            : k.tip === 'RED' ? 'text-red-900'
                            : k.tip === 'BLUE' ? 'text-blue-900'
                            : 'text-text-main';
                          const maddeler = k.icerik.split('\n').map(s => s.trim()).filter(Boolean);
                          return (
                            <div key={k.id} className={`border-2 rounded-xl p-3 ${renk}`}>
                              <h5 className={`font-bold mb-2 ${baslikRenk}`}>{k.baslik}</h5>
                              {maddeler.length > 1 ? (
                                <ul className={`space-y-1 list-disc list-inside ${metinRenk}`}>
                                  {maddeler.map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                              ) : (
                                <p className={`whitespace-pre-wrap ${metinRenk}`}>{k.icerik}</p>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={kurallarKabul}
                        onChange={e => setKurallarKabul(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-primary shrink-0"
                      />
                      <span className="text-sm text-text-main">
                        Yukarıdaki kuralları okudum, anladım ve kabul ediyorum.
                      </span>
                    </label>
                  </div>
                )}

                {modalStep === 'card' && (
                  <div className="mb-8 space-y-4">
                    <button
                      type="button"
                      onClick={() => {
                        clearHold({ release: true });
                        setModalStep('consent');
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-primary-hover transition"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Rıza metnine dön
                    </button>
                    {holdRemaining > 0 && !paymentHtml && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-bold text-amber-900 flex items-center gap-2">
                            Ödeme için kalan süre
                            <span className="font-mono text-base tabular-nums">
                              {String(Math.floor(holdRemaining / 60)).padStart(2, '0')}:
                              {String(holdRemaining % 60).padStart(2, '0')}
                            </span>
                          </div>
                          <div className="text-xs text-amber-800 mt-1">
                            Bu süre boyunca saat dilimi size ayrıldı; tamamlamazsanız başkalarına açılır.
                          </div>
                        </div>
                      </div>
                    )}
                    <h4 className="font-bold flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Kart Bilgileri
                    </h4>
                    <div>
                      <label className="text-xs font-medium text-text-muted mb-1 block">Kart Numarası</label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={23}
                          autoComplete="cc-number"
                          value={cardForm.kart_no}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
                            const formatted = digits.replace(/(.{4})/g, '$1 ').trim();
                            setCardForm(prev => ({ ...prev, kart_no: formatted }));
                            if (cardErrors.kart_no) setCardErrors(prev => ({ ...prev, kart_no: undefined }));
                          }}
                          onBlur={() => setCardErrors(prev => ({ ...prev, kart_no: validateCard().kart_no }))}
                          placeholder="0000 0000 0000 0000"
                          className={`w-full px-4 pr-20 py-3 bg-gray-50 border rounded-lg text-base font-medium focus:outline-none placeholder:font-normal placeholder:text-text-muted ${cardErrors.kart_no ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-primary'}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <CardBrandIcon brand={detectCardBrand(cardForm.kart_no)} />
                        </div>
                      </div>
                      {cardErrors.kart_no && <p className="mt-1 text-xs text-red-600 font-medium">{cardErrors.kart_no}</p>}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-text-muted mb-1 block">Kart Üzerindeki İsim</label>
                      <input
                        type="text"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        maxLength={100}
                        value={cardForm.kart_adi_soyadi}
                        onChange={(e) => {
                          setCardForm(prev => ({ ...prev, kart_adi_soyadi: e.target.value.toUpperCase() }));
                          if (cardErrors.kart_adi_soyadi) setCardErrors(prev => ({ ...prev, kart_adi_soyadi: undefined }));
                        }}
                        onBlur={() => setCardErrors(prev => ({ ...prev, kart_adi_soyadi: validateCard().kart_adi_soyadi }))}
                        placeholder="AD SOYAD"
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg text-base font-medium uppercase focus:outline-none placeholder:font-normal placeholder:text-text-muted ${cardErrors.kart_adi_soyadi ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-primary'}`}
                      />
                      {cardErrors.kart_adi_soyadi && <p className="mt-1 text-xs text-red-600 font-medium">{cardErrors.kart_adi_soyadi}</p>}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium text-text-muted mb-1 block">Ay</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          autoComplete="cc-exp-month"
                          value={cardForm.son_kullanma_ay}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            setCardForm(prev => ({ ...prev, son_kullanma_ay: val }));
                            if (cardErrors.son_kullanma_ay) setCardErrors(prev => ({ ...prev, son_kullanma_ay: undefined }));
                            if (val.length === 2) yilRef.current?.focus();
                          }}
                          onBlur={() => setCardErrors(prev => ({ ...prev, son_kullanma_ay: validateCard().son_kullanma_ay }))}
                          placeholder="MM"
                          className={`w-full px-3 py-3 bg-gray-50 border rounded-lg text-base font-medium text-center focus:outline-none placeholder:font-normal placeholder:text-text-muted ${cardErrors.son_kullanma_ay ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-primary'}`}
                        />
                        {cardErrors.son_kullanma_ay && <p className="mt-1 text-[10px] text-red-600 font-medium leading-tight">{cardErrors.son_kullanma_ay}</p>}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-text-muted mb-1 block">Yıl</label>
                        <input
                          ref={yilRef}
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          autoComplete="cc-exp-year"
                          value={cardForm.son_kullanma_yil}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            setCardForm(prev => ({ ...prev, son_kullanma_yil: val }));
                            if (cardErrors.son_kullanma_yil) setCardErrors(prev => ({ ...prev, son_kullanma_yil: undefined }));
                            if (val.length === 2) cvvRef.current?.focus();
                          }}
                          onBlur={() => setCardErrors(prev => ({ ...prev, son_kullanma_yil: validateCard().son_kullanma_yil }))}
                          placeholder="YY"
                          className={`w-full px-3 py-3 bg-gray-50 border rounded-lg text-base font-medium text-center focus:outline-none placeholder:font-normal placeholder:text-text-muted ${cardErrors.son_kullanma_yil ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-primary'}`}
                        />
                        {cardErrors.son_kullanma_yil && <p className="mt-1 text-[10px] text-red-600 font-medium leading-tight">{cardErrors.son_kullanma_yil}</p>}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-text-muted mb-1 block">CVV</label>
                        <input
                          ref={cvvRef}
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          autoComplete="cc-csc"
                          value={cardForm.cvv}
                          onChange={(e) => {
                            setCardForm(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }));
                            if (cardErrors.cvv) setCardErrors(prev => ({ ...prev, cvv: undefined }));
                          }}
                          onBlur={() => setCardErrors(prev => ({ ...prev, cvv: validateCard().cvv }))}
                          placeholder="123"
                          className={`w-full px-3 py-3 bg-gray-50 border rounded-lg text-base font-medium text-center focus:outline-none placeholder:font-normal placeholder:text-text-muted ${cardErrors.cvv ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-primary'}`}
                        />
                        {cardErrors.cvv && <p className="mt-1 text-[10px] text-red-600 font-medium leading-tight">{cardErrors.cvv}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {modalStep === 'iban' && (
                  <div className="mb-8 space-y-4">
                    <button
                      type="button"
                      onClick={() => setModalStep('consent')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-primary-hover transition"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Rıza metnine dön
                    </button>
                    <h4 className="font-bold flex items-center gap-2">
                      <Landmark className="w-5 h-5 text-primary" />
                      Online Ödeme Bilgileri
                    </h4>
                    <p className="text-sm text-text-muted">
                      Ödemenizi aşağıdaki banka hesabına havale/EFT yoluyla gerçekleştirebilirsiniz.
                    </p>

                    {ibanInfo ? (
                      <div className="bg-primary/5 rounded-xl p-4 space-y-3">
                        {([
                          { key: 'banka', label: 'Banka', value: ibanInfo.banka, copyValue: ibanInfo.banka, mono: false },
                          { key: 'alici', label: 'Alıcı', value: ibanInfo.alici, copyValue: ibanInfo.alici, mono: false },
                          { key: 'iban', label: 'IBAN', value: ibanInfo.iban, copyValue: ibanInfo.iban.replace(/\s+/g, ''), mono: true },
                        ] as const).map(row => (
                          <div key={row.key}>
                            <div className="text-xs text-text-muted">{row.label}</div>
                            <div className="flex items-center gap-2">
                              <div className={`flex-1 font-medium ${row.mono ? 'font-mono text-sm' : ''}`}>
                                {row.value}
                              </div>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(row.copyValue, row.key)}
                                className={`p-2 rounded-lg transition shrink-0 ${
                                  copiedField === row.key
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-white text-primary hover:bg-primary/10'
                                }`}
                                title={row.label + "'ı kopyala"}
                              >
                                {copiedField === row.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                      </div>
                    )}

                    {(() => {
                      const tc = user?.tc;
                      const dogumIso = user?.dogum_tarihi;
                      const dogumText = dogumIso
                        ? format(new Date(dogumIso + 'T00:00:00'), 'dd/MM/yyyy')
                        : null;
                      const rezervasyonTarih = format(selectedDate, 'dd/MM/yyyy');
                      const saat = selectedSlot?.time ?? '';
                      const kortAdi = selectedCourt?.ad ?? '';
                      const eksik = !tc || !dogumText || !saat || !kortAdi;
                      const aciklama = eksik
                        ? ''
                        : `${rezervasyonTarih} - ${saat} – ${kortAdi} – ${tc} – ${dogumText} - Tenis Kortu Ücreti Ödemesi`;

                      return (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 text-sm">
                          <div className="font-bold text-amber-900">
                            Açıklama Kısmına Yazılması Gereken Bilgiler
                          </div>
                          <div className="text-amber-800">
                            Rezervasyon Tarihi - Saati – Kort Adı – T.C. Kimlik Numarası – Doğum Tarihi (Gün/Ay/Yıl)
                          </div>
                          <div className="pt-3 border-t border-amber-200">
                            <div className="text-xs font-bold text-amber-900 mb-1">Sizin Açıklamanız:</div>
                            {eksik ? (
                              <div className="text-xs text-amber-700">
                                Açıklama oluşturulamadı (kullanıcı bilgileri eksik).
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 bg-white rounded-lg p-2">
                                <div className="font-mono text-xs text-amber-900 flex-1 break-all">
                                  {aciklama}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(aciklama, 'aciklama')}
                                  className={`p-2 rounded-lg transition shrink-0 ${
                                    copiedField === 'aciklama'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                  }`}
                                  title="Açıklamayı kopyala"
                                >
                                  {copiedField === 'aciklama' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="pt-3 border-t border-amber-200 text-xs text-amber-800">
                            Ödeme işleminizin sistem tarafından doğru şekilde eşleştirilebilmesi için
                            açıklama kısmının eksiksiz doldurulması gerekmektedir.
                          </div>
                        </div>
                      );
                    })()}

                    <div>
                      <label className="text-xs font-medium text-text-muted mb-2 block">
                        Dekont (yalnızca PDF, maks. 5 MB)
                      </label>
                      <label className={`block w-full p-5 border-2 border-dashed rounded-xl cursor-pointer transition ${
                        dekontFile ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50 hover:border-primary/50'
                      }`}>
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0] ?? null;
                            if (!f) { setDekontFile(null); return; }
                            if (f.type !== 'application/pdf' || !f.name.toLowerCase().endsWith('.pdf')) {
                              showError('Sadece PDF dosyası kabul edilir.');
                              e.target.value = '';
                              return;
                            }
                            if (f.size > 5 * 1024 * 1024) {
                              showError('Dosya 5 MB\'dan büyük olamaz.');
                              e.target.value = '';
                              return;
                            }
                            setDekontFile(f);
                          }}
                        />
                        <div className="flex items-center gap-3">
                          {dekontFile ? (
                            <>
                              <FileText className="w-8 h-8 text-primary shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{dekontFile.name}</div>
                                <div className="text-xs text-text-muted">
                                  {(dekontFile.size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                              <span className="text-xs text-primary font-bold">Değiştir</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-text-muted shrink-0" />
                              <div className="flex-1 text-sm text-text-muted">
                                <div className="font-medium">PDF dosyası seçin</div>
                                <div className="text-xs">Ekran görüntüsü kabul edilmez</div>
                              </div>
                            </>
                          )}
                        </div>
                      </label>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                      Dekontunuz yüklendikten sonra rezervasyonunuz <strong>onay bekleyen</strong> duruma alınır.
                      Admin onayından sonra aktifleşir.
                    </div>
                  </div>
                )}

                <div className="bg-primary/5 p-5 rounded-xl space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-muted">Rezervasyon Ücreti</span>
                    <span className="font-bold text-lg text-primary">{selectedSlot?.fiyat ?? selectedCourt?.fiyat} ₺</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-primary/10">
                    <span className="font-bold">Toplam</span>
                    <span className="font-black text-2xl text-primary">{selectedSlot?.fiyat ?? selectedCourt?.fiyat} ₺</span>
                  </div>
                </div>
              </div>

              <div
                className="px-6 pt-2"
                style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
              >
                {modalStep === 'slot' && (
                  <button
                    disabled={!selectedSlot}
                    onClick={() => setModalStep('method')}
                    className={`w-full py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                      !selectedSlot
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white shadow-premium hover:bg-primary-hover active:scale-95'
                    }`}
                  >
                    Devam Et
                  </button>
                )}
                {modalStep === 'consent' && (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setPendingMethod(null);
                        setKurallarKabul(false);
                        setModalStep('method');
                      }}
                      className="w-full py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 bg-gray-100 text-text-main hover:bg-gray-200 active:scale-95"
                    >
                      Reddet
                    </button>
                    <button
                      type="button"
                      disabled={!kurallarKabul || acquiringHold}
                      onClick={async () => {
                        if (pendingMethod === 'iban') {
                          fetchIbanBilgisi();
                          setModalStep('iban');
                          return;
                        }
                        // Kart akışı — ödeme ekranına geçmeden önce slot'u 5 dk kilitle.
                        const ok = await acquireHold();
                        if (ok) setModalStep('card');
                      }}
                      className={`w-full py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                        kurallarKabul && !acquiringHold
                          ? 'bg-primary text-white shadow-premium hover:bg-primary-hover active:scale-95'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {acquiringHold ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Kabul Ediyorum'}
                    </button>
                  </div>
                )}
                {modalStep === 'card' && (
                  <button
                    disabled={isReserving}
                    onClick={handleStartPayment}
                    className="w-full py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 bg-primary text-white shadow-premium hover:bg-primary-hover active:scale-95 disabled:opacity-60"
                  >
                    {isReserving ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Ödemeyi Tamamla'}
                  </button>
                )}
                {modalStep === 'iban' && (
                  <button
                    disabled={isReserving || !dekontFile}
                    onClick={handleIbanSubmit}
                    className={`w-full py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                      !dekontFile
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-primary text-white shadow-premium hover:bg-primary-hover active:scale-95 disabled:opacity-60'
                    }`}
                  >
                    {isReserving ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Dekontu Gönder'}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {paymentHtml && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/80 flex flex-col"
          >
            <div
              className="flex items-center justify-between px-4 pb-4 bg-white"
              style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 2.75rem)' }}
            >
              <span className="font-bold text-text-main">3D Secure Ödeme</span>
              <button
                disabled
                aria-disabled="true"
                title="Ödeme sürerken kapatılamaz"
                className="p-2 bg-gray-100 rounded-full opacity-40 cursor-not-allowed"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <iframe
              title="Ödeme"
              srcDoc={paymentHtml}
              className="flex-1 w-full bg-white"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {galleryIndex !== null && (() => {
          const images: { src: string; title: string }[] = galeri.length > 0
            ? galeri.map(g => ({ src: g.dosya_url, title: g.baslik ?? '' }))
            : [{ src: bannerUrl ?? COURT_IMAGE_FALLBACK, title: bannerBaslik }];
          const total = images.length;
          const i = Math.max(0, Math.min(galleryIndex, total - 1));
          const cur = images[i];
          const prev = () => setGalleryIndex(i === 0 ? total - 1 : i - 1);
          const next = () => setGalleryIndex(i === total - 1 ? 0 : i + 1);
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] bg-black flex items-center justify-center"
              onClick={() => setGalleryIndex(null)}
            >
              <button
                onClick={(e) => { e.stopPropagation(); setGalleryIndex(null); }}
                style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
                className="absolute right-4 z-20 w-11 h-11 bg-black/60 hover:bg-black/80 rounded-full text-white transition flex items-center justify-center ring-1 ring-white/20"
                title="Kapat"
              >
                <X className="w-5 h-5" />
              </button>

              <motion.img
                key={cur.src}
                src={cur.src}
                alt={cur.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                drag={total > 1 ? 'x' : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.35}
                dragMomentum={false}
                onDragEnd={(_, info) => {
                  if (info.offset.x > 60) prev();
                  else if (info.offset.x < -60) next();
                }}
                className="max-w-full max-h-full object-contain touch-pan-x select-none"
                draggable={false}
                onClick={(e) => e.stopPropagation()}
              />

              {total > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition"
                  >
                    <ChevronRight className="w-6 h-6 rotate-180" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              <div className="absolute bottom-0 left-0 right-0 px-6 pb-6 pt-12 bg-gradient-to-t from-black/90 to-transparent text-center text-white safe-area-bottom">
                <div className="font-bold text-lg">{cur.title}</div>
                {total > 1 && (
                  <div className="text-xs text-white/70 mt-1">{i + 1} / {total}</div>
                )}
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      <AnimatePresence>
        {showKurallar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full rounded-t-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <ScrollText className="w-5 h-5 text-primary" />
                  <h3 className="font-bold text-lg">Kurallar</h3>
                </div>
                <button onClick={() => setShowKurallar(false)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="overflow-y-auto p-5 space-y-4 text-sm">
                {kurallarLoading ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
                ) : kurallar.length === 0 ? (
                  <div className="text-center py-10 text-text-muted text-sm">
                    Henüz tanımlı kural bulunmamaktadır.
                  </div>
                ) : (
                  kurallar.map(k => {
                    const renk = k.tip === 'AMBER' ? 'border-amber-200 bg-amber-50'
                      : k.tip === 'RED' ? 'border-red-200 bg-red-50'
                      : k.tip === 'BLUE' ? 'border-blue-200 bg-blue-50'
                      : 'border-primary/20 bg-primary/5';
                    const baslikRenk = k.tip === 'AMBER' ? 'text-amber-900'
                      : k.tip === 'RED' ? 'text-red-900'
                      : k.tip === 'BLUE' ? 'text-blue-900'
                      : 'text-primary';
                    const metinRenk = k.tip === 'AMBER' ? 'text-amber-900'
                      : k.tip === 'RED' ? 'text-red-900'
                      : k.tip === 'BLUE' ? 'text-blue-900'
                      : 'text-text-main';
                    const maddeler = k.icerik.split('\n').map(s => s.trim()).filter(Boolean);
                    return (
                      <div key={k.id} className={`border-2 rounded-xl p-4 ${renk}`}>
                        <h4 className={`font-bold mb-2 ${baslikRenk}`}>{k.baslik}</h4>
                        {maddeler.length > 1 ? (
                          <ul className={`space-y-2 list-disc list-inside ${metinRenk}`}>
                            {maddeler.map((m, i) => <li key={i}>{m}</li>)}
                          </ul>
                        ) : (
                          <p className={`whitespace-pre-wrap ${metinRenk}`}>{k.icerik}</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDuyurular && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full rounded-t-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  <h3 className="font-bold text-lg">Duyurular</h3>
                </div>
                <button onClick={() => setShowDuyurular(false)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="overflow-y-auto p-5">
                {duyurularLoading ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
                ) : duyurular.length === 0 ? (
                  <div className="text-center py-10 text-text-muted text-sm">
                    Aktif duyuru bulunmamaktadır.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {duyurular.map(d => {
                      const renk = d.tip === 'UYARI' ? 'border-amber-200 bg-amber-50'
                        : d.tip === 'BAKIM' ? 'border-red-200 bg-red-50'
                        : 'border-blue-200 bg-blue-50';
                      const textRenk = d.tip === 'UYARI' ? 'text-amber-900'
                        : d.tip === 'BAKIM' ? 'text-red-900'
                        : 'text-blue-900';
                      return (
                        <div key={d.id} className={`p-4 rounded-xl border-2 ${renk}`}>
                          <h4 className={`font-bold ${textRenk}`}>{d.baslik}</h4>
                          <p className={`text-sm mt-1 whitespace-pre-wrap ${textRenk}`}>{d.icerik}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cancelTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-65 flex items-end justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => { if (cancellingId === null) setCancelTarget(null); }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full rounded-t-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">Rezervasyonu İptal Et</h3>
                    <p className="text-sm text-text-muted mt-1">
                      Bu rezervasyonu iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 text-sm space-y-1">
                  <div className="font-bold">{cancelTarget.kort?.ad}</div>
                  <div className="text-text-muted flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {cancelTarget.tarih_text}
                  </div>
                  <div className="text-text-muted flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {cancelTarget.baslangic_saat} - {cancelTarget.bitis_saat}
                  </div>
                </div>

                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <span>
                    <b>Dikkat:</b> Rezervasyonu iptal ettiğinizde ödemeniz iade edilmez.
                  </span>
                </div>
              </div>

              <div
                className="px-6 pt-2 flex gap-3"
                style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
              >
                <button
                  disabled={cancellingId !== null}
                  onClick={() => setCancelTarget(null)}
                  className="flex-1 py-4 rounded-xl font-bold border-2 border-gray-200 text-text-muted hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Vazgeç
                </button>
                <button
                  disabled={cancellingId !== null}
                  onClick={() => handleCancel(cancelTarget.id)}
                  className="flex-1 py-4 rounded-xl font-bold bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {cancellingId === cancelTarget.id ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Evet, İptal Et'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {reUploadFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full rounded-t-2xl overflow-hidden flex flex-col max-h-[95vh]"
            >
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg">Yeniden Dekont Yükle</h3>
                    <p className="text-xs text-text-muted mt-1">
                      {reUploadFor.kort?.ad} · {reUploadFor.tarih_text} · {reUploadFor.baslangic_saat}
                    </p>
                  </div>
                  <button
                    disabled={reUploading}
                    onClick={() => { setReUploadFor(null); setReUploadFile(null); }}
                    className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition disabled:opacity-50"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {reUploadFor.son_red_sebebi && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                    <div className="text-xs font-bold text-red-800">Önceki Red Sebebi</div>
                    <div className="text-red-700">{reUploadFor.son_red_sebebi}</div>
                  </div>
                )}

                {ibanInfo ? (
                  <div className="bg-primary/5 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-primary">
                      <Landmark className="w-4 h-4" />
                      Ödeme Bilgileri
                    </div>
                    {([
                      { key: 're_banka', label: 'Banka', value: ibanInfo.banka, copyValue: ibanInfo.banka, mono: false },
                      { key: 're_alici', label: 'Alıcı', value: ibanInfo.alici, copyValue: ibanInfo.alici, mono: false },
                      { key: 're_iban', label: 'IBAN', value: ibanInfo.iban, copyValue: ibanInfo.iban.replace(/\s+/g, ''), mono: true },
                    ] as const).map(row => (
                      <div key={row.key}>
                        <div className="text-xs text-text-muted">{row.label}</div>
                        <div className="flex items-center gap-2">
                          <div className={`flex-1 font-medium ${row.mono ? 'font-mono text-sm break-all' : ''}`}>
                            {row.value}
                          </div>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(row.copyValue, row.key)}
                            className={`p-2 rounded-lg transition shrink-0 ${
                              copiedField === row.key
                                ? 'bg-green-100 text-green-700'
                                : 'bg-white text-primary hover:bg-primary/10'
                            }`}
                            title={row.label + "'ı kopyala"}
                          >
                            {copiedField === row.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  </div>
                )}

                {(() => {
                  const tc = user?.tc;
                  const dogumIso = user?.dogum_tarihi;
                  const dogumText = dogumIso
                    ? format(new Date(dogumIso + 'T00:00:00'), 'dd/MM/yyyy')
                    : null;
                  const rezervasyonTarih = reUploadFor.tarih
                    ? format(new Date(reUploadFor.tarih + 'T00:00:00'), 'dd/MM/yyyy')
                    : '';
                  const saat = reUploadFor.baslangic_saat ?? '';
                  const kortAdi = reUploadFor.kort?.ad ?? '';
                  const eksik = !tc || !dogumText || !saat || !rezervasyonTarih || !kortAdi;
                  const aciklama = eksik
                    ? ''
                    : `${rezervasyonTarih} - ${saat} – ${kortAdi} – ${tc} – ${dogumText} - Tenis Kortu Ücreti Ödemesi`;

                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 text-sm">
                      <div className="font-bold text-amber-900">
                        Açıklama Kısmına Yazılması Gereken Bilgiler
                      </div>
                      <div className="text-amber-800">
                        Rezervasyon Tarihi - Saati – Kort Adı – T.C. Kimlik Numarası – Doğum Tarihi (Gün/Ay/Yıl)
                      </div>
                      <div className="pt-3 border-t border-amber-200">
                        <div className="text-xs font-bold text-amber-900 mb-1">Sizin Açıklamanız:</div>
                        {eksik ? (
                          <div className="text-xs text-amber-700">
                            Açıklama oluşturulamadı (kullanıcı bilgileri eksik).
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 bg-white rounded-lg p-2">
                            <div className="font-mono text-xs text-amber-900 flex-1 break-all">
                              {aciklama}
                            </div>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(aciklama, 're_aciklama')}
                              className={`p-2 rounded-lg transition shrink-0 ${
                                copiedField === 're_aciklama'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                              }`}
                              title="Açıklamayı kopyala"
                            >
                              {copiedField === 're_aciklama' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="pt-3 border-t border-amber-200 text-xs text-amber-800">
                        Ödeme işleminizin sistem tarafından doğru şekilde eşleştirilebilmesi için
                        açıklama kısmının eksiksiz doldurulması gerekmektedir.
                      </div>
                    </div>
                  );
                })()}

                <label className={`block w-full p-5 border-2 border-dashed rounded-xl cursor-pointer transition ${
                  reUploadFile ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50 hover:border-primary/50'
                }`}>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      if (!f) { setReUploadFile(null); return; }
                      if (f.type !== 'application/pdf' || !f.name.toLowerCase().endsWith('.pdf')) {
                        showError('Sadece PDF dosyası kabul edilir.');
                        e.target.value = '';
                        return;
                      }
                      if (f.size > 5 * 1024 * 1024) {
                        showError('Dosya 5 MB\'dan büyük olamaz.');
                        e.target.value = '';
                        return;
                      }
                      setReUploadFile(f);
                    }}
                  />
                  <div className="flex items-center gap-3">
                    {reUploadFile ? (
                      <>
                        <FileText className="w-8 h-8 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{reUploadFile.name}</div>
                          <div className="text-xs text-text-muted">{(reUploadFile.size / 1024).toFixed(1)} KB</div>
                        </div>
                        <span className="text-xs text-primary font-bold">Değiştir</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-text-muted shrink-0" />
                        <div className="flex-1 text-sm text-text-muted">
                          <div className="font-medium">Yeni PDF dekontu seçin</div>
                          <div className="text-xs">Maks 5 MB, sadece PDF</div>
                        </div>
                      </>
                    )}
                  </div>
                </label>

                <button
                  disabled={reUploading || !reUploadFile}
                  onClick={handleReUpload}
                  className={`w-full py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-3 ${
                    !reUploadFile
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white shadow-premium hover:bg-primary-hover active:scale-95 disabled:opacity-60'
                  }`}
                >
                  {reUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Dekontu Gönder'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {ibanSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white w-full max-w-xs rounded-2xl p-8 text-center shadow-premium"
            >
              <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Dekont Alındı</h3>
              <p className="text-text-muted text-sm mb-8">
                Dekontunuz başarıyla yüklendi. Admin onayından sonra rezervasyonunuz aktifleşecek ve sizi bilgilendireceğiz.
              </p>
              <button
                onClick={() => {
                  setIbanSuccess(false);
                  setActiveTab('reservations');
                }}
                className="w-full py-4 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/20 transition active:scale-95"
              >
                Rezervasyonlarıma Git
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {reservationSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white w-full max-w-xs rounded-2xl p-8 text-center shadow-premium"
            >
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Harika!</h3>
              <p className="text-text-muted text-sm mb-8">
                Rezervasyonun başarıyla oluşturuldu. Maç saatin yaklaştığında sana hatırlatma göndereceğiz.
              </p>
              <button
                onClick={() => {
                  setReservationSuccess(false);
                  setShowReservationModal(false);
                  setSelectedSlot(null);
                  setActiveTab('reservations');
                }}
                className="w-full py-4 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/20 transition active:scale-95"
              >
                Rezervasyonlarıma Git
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

type Yog = { oran: number; dolu: number; toplam: number; tatil: boolean } | undefined;

function KortDonut({ yog }: { yog: Yog }) {
  if (!yog) return <div className="w-9 h-9" />;
  const size = 36;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const oran = yog.tatil ? 1 : Math.max(0, Math.min(1, yog.oran));
  const offset = c * (1 - oran);

  const ringColor = yog.tatil
    ? '#9ca3af'
    : oran >= 0.85
      ? '#ef4444'
      : oran >= 0.5
        ? '#f59e0b'
        : '#10b981';
  const label = yog.tatil ? 'Kapalı' : `%${Math.round(oran * 100)}`;
  const labelRenk = yog.tatil
    ? 'text-gray-500'
    : oran >= 0.85
      ? 'text-red-600'
      : oran >= 0.5
        ? 'text-amber-600'
        : 'text-emerald-600';

  return (
    <div className="flex items-center gap-1.5" title={yog.tatil ? 'Kapalı' : `${yog.dolu}/${yog.toplam} dolu`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(0,0,0,0.08)" strokeWidth={stroke} fill="none" />
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke={ringColor} strokeWidth={stroke} fill="none"
            strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-[9px] font-bold ${labelRenk}`}>
          {yog.tatil ? '×' : `${Math.round(oran * 100)}`}
        </span>
      </div>
      <span className={`text-[10px] font-bold ${labelRenk}`}>{label}</span>
    </div>
  );
}

export default App;
